import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Typography,
  Paper,
  Autocomplete,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, DeleteOutline as DeleteOutlineIcon, AttachFile, Edit as EditIcon } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import ADSBChecksManager from './ADSBChecksManager';
import ScheduleRowLastPerformedFields from './ScheduleRowLastPerformedFields';
import ScheduleTlbCycleCounterField from '../shared/ScheduleTlbCycleCounterField';
import ScheduleTlbHoursCounterField from '../shared/ScheduleTlbHoursCounterField';
import {
  CLASSIFICATION_OPTIONS,
  PRIORITY_OPTIONS,
  SOURCE_OPTIONS,
  REGULATORY_AUTHORITY_OPTIONS
} from './constants/adsbConstants';

/** Format lastDoneValue from API for form: Date/ISO string → YYYY-MM-DD; number unchanged. */
const formatLastDoneValueForForm = (recurrenceBasedOn, lastDoneValue) => {
  if (lastDoneValue == null || lastDoneValue === '') return '';
  if (['DAYS', 'MONTHS', 'YEARS'].includes(recurrenceBasedOn)) {
    const iso = typeof lastDoneValue === 'string' ? lastDoneValue : (lastDoneValue instanceof Date ? lastDoneValue.toISOString() : '');
    return iso && iso.length >= 10 ? iso.slice(0, 10) : '';
  }
  return lastDoneValue;
};

/** True when schedule row has Based On set, so "Last performed at" has a clear unit. */
const isScheduleRowValid = (s) => !!s.recurrenceBasedOn;

const CreateEditADSBulletinDialog = ({
  open,
  onClose,
  onSuccess,
  onEvidenceUpload,
  bulletin,
  aircraft = [],
  initialFilters = {},
  readOnly = false,
  onSwitchToEdit
}) => {
  const defaultScheduleRow = () => ({
    recurrenceBasedOn: '',
    matchType: 'EXACTLY',
    recurrenceValue: '',
    recurrenceValue2: '',
    tlbCarriedForwardKey: '',
    lastDoneValue: '',
    lastDoneNote: '',
    lastDoneByAircraft: []
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    classification: '',
    priority: '',
    source: '',
    sourceCustom: '',
    regulatoryAuthorityName: '',
    regulatoryAuthorityNameCustom: '',
    serialNumbersMakeModel: '',
    aircraftAssemblies: [],
    recurrenceType: 'ONE_TIME',
    oneTimeStatus: 'INCOMPLETE',
    schedules: [{ recurrenceBasedOn: '', matchType: 'EXACTLY', recurrenceValue: '', recurrenceValue2: '', tlbCarriedForwardKey: '', lastDoneValue: '', lastDoneNote: '', lastDoneByAircraft: [] }],
    bulletinApplicable: true,
    bulletinApplicabilityRemark: '',
    adsbChecks: []
  });
  const [assembliesByAircraft, setAssembliesByAircraft] = useState({});
  const [loadingAssemblies, setLoadingAssemblies] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasWorkOrders, setHasWorkOrders] = useState(false);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyIntimations, setHistoryIntimations] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [deletingEvidence, setDeletingEvidence] = useState(null);
  const [currentUsageTotals, setCurrentUsageTotals] = useState(null);
  const { showToast } = useToast();

  const primaryAircraftIdForTlb =
    formData.aircraftAssemblies[0]?.aircraftId?._id ||
    formData.aircraftAssemblies[0]?.aircraftId ||
    '';

  useEffect(() => {
    if (!open || !primaryAircraftIdForTlb) {
      setCurrentUsageTotals(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/aircraft/${primaryAircraftIdForTlb}/live-usage`)
      .then((res) => {
        if (!cancelled) setCurrentUsageTotals(res.data?.currentUsageTotals || null);
      })
      .catch(() => {
        if (!cancelled) setCurrentUsageTotals(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, primaryAircraftIdForTlb]);

  useEffect(() => {
    if (open) setTabIndex(0);
  }, [open]);

  useEffect(() => {
    if (!bulletin || tabIndex !== 1) return;
    let cancelled = false;
    const id = bulletin._id;
    setHistoryLoading(true);
    setHistoryError(null);
    Promise.all([
      axios.get(`/api/adsb-service-bulletins/${id}/history`),
      axios.get('/api/intimations', { params: { sourceType: 'ADSB_SERVICE_BULLETIN', sourceId: String(id) } })
    ]).then(([historyRes, intimationsRes]) => {
      if (!cancelled) {
        setHistoryEvents(Array.isArray(historyRes.data) ? historyRes.data : []);
        setHistoryIntimations(Array.isArray(intimationsRes.data) ? intimationsRes.data : []);
      }
    }).catch((err) => {
      if (!cancelled) setHistoryError(err.response?.data?.message || 'Failed to load history');
    }).finally(() => {
      if (!cancelled) setHistoryLoading(false);
    });
    return () => { cancelled = true; };
  }, [bulletin?._id, tabIndex]);

  const handleEvidenceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !bulletin?._id) return;
    setUploadingEvidence(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`/api/adsb-service-bulletins/${bulletin._id}/evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Evidence file uploaded', 'success');
      if (onEvidenceUpload) onEvidenceUpload(res.data);
      e.target.value = '';
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleEvidenceDelete = async (fileName) => {
    if (!bulletin?._id || !fileName) return;
    setDeletingEvidence(fileName);
    try {
      const res = await axios.delete(`/api/adsb-service-bulletins/${bulletin._id}/evidence/${encodeURIComponent(fileName)}`);
      showToast('Evidence file removed', 'success');
      if (onEvidenceUpload) onEvidenceUpload(res.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove file', 'error');
    } finally {
      setDeletingEvidence(null);
    }
  };

  const fetchAssemblies = async (aircraftId) => {
    if (!aircraftId || assembliesByAircraft[aircraftId]) return;
    setLoadingAssemblies(true);
    try {
      const res = await axios.get('/api/assemblies', { params: { aircraftId, isActive: 'true' } });
      setAssembliesByAircraft(prev => ({ ...prev, [aircraftId]: res.data }));
    } catch (err) {
      console.error(err);
      showToast('Failed to fetch assemblies', 'error');
    } finally {
      setLoadingAssemblies(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (bulletin) {
      const aa = (bulletin.aircraftAssemblies || []).map(a => ({
        aircraftId: a.aircraftId?._id || a.aircraftId,
        assemblyIds: (a.assemblyIds || []).map(x => x && (x._id || x))
      }));
      const schedulesList = bulletin.schedules && bulletin.schedules.length > 0
        ? bulletin.schedules.map(s => ({
            recurrenceBasedOn: s.recurrenceBasedOn || '',
            matchType: s.matchType || 'EXACTLY',
            recurrenceValue: s.recurrenceBasedOn === 'HOURS' ? (s.recurrenceValue != null ? Math.floor(Number(s.recurrenceValue)) : '') : (s.recurrenceValue ?? ''),
            recurrenceValue2: s.recurrenceBasedOn === 'HOURS' && s.recurrenceValue2 != null ? Math.floor(Number(s.recurrenceValue2)) : (s.recurrenceValue2 ?? ''),
            lastDoneValue: ['DAYS', 'MONTHS', 'YEARS'].includes(s.recurrenceBasedOn) ? formatLastDoneValueForForm(s.recurrenceBasedOn, s.lastDoneValue) : (s.lastDoneValue ?? ''),
            lastDoneNote: s.lastDoneNote != null ? String(s.lastDoneNote) : '',
            tlbCarriedForwardKey: s.tlbCarriedForwardKey || '',
            lastDoneByAircraft: Array.isArray(s.lastDoneByAircraft)
              ? s.lastDoneByAircraft
                  .filter((x) => x && x.aircraftId)
                  .map((x) => ({
                    aircraftId: x.aircraftId?._id || x.aircraftId,
                    lastDoneValue: ['DAYS', 'MONTHS', 'YEARS'].includes(s.recurrenceBasedOn)
                      ? formatLastDoneValueForForm(s.recurrenceBasedOn, x.lastDoneValue)
                      : (x.lastDoneValue ?? ''),
                    lastDoneNote: x.lastDoneNote != null ? String(x.lastDoneNote) : ''
                  }))
              : []
          }))
        : [{
            recurrenceBasedOn: bulletin.recurrenceBasedOn || '',
            matchType: bulletin.matchType || 'EXACTLY',
            recurrenceValue: bulletin.recurrenceValue ?? '',
            recurrenceValue2: bulletin.recurrenceValue2 ?? '',
            lastDoneValue: '',
            lastDoneNote: '',
            lastDoneByAircraft: []
          }];
      setFormData({
        title: bulletin.title || '',
        description: bulletin.description || '',
        classification: bulletin.classification || '',
        priority: bulletin.priority || '',
        source: bulletin.source || '',
        sourceCustom: bulletin.sourceCustom || '',
        regulatoryAuthorityName: bulletin.regulatoryAuthorityName || '',
        regulatoryAuthorityNameCustom: bulletin.regulatoryAuthorityNameCustom || '',
        serialNumbersMakeModel: bulletin.serialNumbersMakeModel || '',
        aircraftAssemblies: aa,
        recurrenceType: bulletin.recurrenceType || 'ONE_TIME',
        oneTimeStatus: bulletin.oneTimeStatus || (bulletin.isClosed ? 'COMPLETED' : 'INCOMPLETE'),
        schedules: schedulesList,
        bulletinApplicable: bulletin.bulletinApplicable !== false,
        bulletinApplicabilityRemark: bulletin.bulletinApplicabilityRemark || '',
        adsbChecks: bulletin.adsbChecks || []
      });
      setHasWorkOrders(!!(bulletin.workOrders && bulletin.workOrders.length > 0));
      aa.forEach(a => fetchAssemblies(a.aircraftId));
    } else {
      setFormData({
        title: '',
        description: '',
        classification: '',
        priority: '',
        source: '',
        sourceCustom: '',
        regulatoryAuthorityName: '',
        regulatoryAuthorityNameCustom: '',
        serialNumbersMakeModel: '',
        aircraftAssemblies: [],
        recurrenceType: 'ONE_TIME',
        oneTimeStatus: 'INCOMPLETE',
        schedules: [{ recurrenceBasedOn: '', matchType: 'EXACTLY', recurrenceValue: '', recurrenceValue2: '', tlbCarriedForwardKey: '', lastDoneValue: '', lastDoneNote: '', lastDoneByAircraft: [] }],
        bulletinApplicable: true,
        bulletinApplicabilityRemark: '',
        adsbChecks: []
      });
      setHasWorkOrders(false);
      setAssembliesByAircraft({});
    }
    setError('');
  }, [open, bulletin]);

  const addAircraft = (aircraftId) => {
    if (!aircraftId) return;
    const exists = formData.aircraftAssemblies.some(a => (a.aircraftId && a.aircraftId.toString()) === aircraftId.toString());
    if (exists) return;
    setFormData({
      ...formData,
      aircraftAssemblies: [...formData.aircraftAssemblies, { aircraftId, assemblyIds: [] }]
    });
    fetchAssemblies(aircraftId);
  };

  const removeAircraft = (index) => {
    const removedAircraftId = formData.aircraftAssemblies[index]?.aircraftId?.toString();
    const nextAircraftAssemblies = formData.aircraftAssemblies.filter((_, i) => i !== index);
    const nextSchedules = formData.schedules.map((s) => ({
      ...s,
      lastDoneByAircraft: (s.lastDoneByAircraft || []).filter((x) => x?.aircraftId?.toString() !== removedAircraftId)
    }));
    setFormData({ ...formData, aircraftAssemblies: nextAircraftAssemblies, schedules: nextSchedules });
  };

  const setAssemblyIdsForAircraft = (index, assemblyIds) => {
    const next = [...formData.aircraftAssemblies];
    next[index] = { ...next[index], assemblyIds: assemblyIds.map(id => id && (id._id || id)) };
    setFormData({ ...formData, aircraftAssemblies: next });
  };

  const handleScheduleChange = (index, field, value) => {
    const next = formData.schedules.map((s, i) => (i === index ? { ...s, [field]: value } : s));
    setFormData({ ...formData, schedules: next });
  };

  const handleScheduleAircraftLastDoneChange = (scheduleIndex, aircraftId, field, value) => {
    const aid = aircraftId && aircraftId.toString();
    if (!aid) return;
    const next = formData.schedules.map((s, i) => {
      if (i !== scheduleIndex) return s;
      const existing = Array.isArray(s.lastDoneByAircraft) ? s.lastDoneByAircraft : [];
      const rowIdx = existing.findIndex((x) => x?.aircraftId?.toString() === aid);
      if (rowIdx >= 0) {
        const updated = [...existing];
        updated[rowIdx] = { ...updated[rowIdx], [field]: value };
        return { ...s, lastDoneByAircraft: updated };
      }
      return {
        ...s,
        lastDoneByAircraft: [...existing, { aircraftId: aid, lastDoneValue: '', lastDoneNote: '', [field]: value }]
      };
    });
    setFormData({ ...formData, schedules: next });
  };

  const applyScheduleLastDoneToAllAircraft = (scheduleIndex, sourceAircraftId) => {
    const aid = sourceAircraftId && sourceAircraftId.toString();
    if (!aid) return;
    const schedule = formData.schedules[scheduleIndex];
    if (!schedule) return;
    const source = (schedule.lastDoneByAircraft || []).find((x) => x?.aircraftId?.toString() === aid);
    const sourceValue = source?.lastDoneValue ?? schedule.lastDoneValue ?? '';
    const sourceNote = source?.lastDoneNote ?? schedule.lastDoneNote ?? '';
    const allAircraftIds = formData.aircraftAssemblies
      .map((x) => x?.aircraftId && x.aircraftId.toString())
      .filter(Boolean);
    const next = formData.schedules.map((s, i) => i === scheduleIndex
      ? {
          ...s,
          lastDoneByAircraft: allAircraftIds.map((x) => ({
            aircraftId: x,
            lastDoneValue: sourceValue,
            lastDoneNote: sourceNote
          }))
        }
      : s);
    setFormData({ ...formData, schedules: next });
  };

  const handleAddSchedule = () => {
    setFormData({ ...formData, schedules: [...formData.schedules, defaultScheduleRow()] });
  };

  const handleRemoveSchedule = (index) => {
    if (formData.schedules.length <= 1) return;
    setFormData({ ...formData, schedules: formData.schedules.filter((_, i) => i !== index) });
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.classification) {
      setError('Classification is required');
      return;
    }
    if (!formData.priority) {
      setError('Priority is required');
      return;
    }
    if (!formData.source) {
      setError('Source is required');
      return;
    }
    if (formData.aircraftAssemblies.length === 0) {
      setError('At least one aircraft is required');
      return;
    }
    if (!formData.adsbChecks || formData.adsbChecks.length === 0) {
      setError('At least one AD/SB check is required');
      return;
    }
    if (!formData.bulletinApplicable && !(formData.bulletinApplicabilityRemark && formData.bulletinApplicabilityRemark.trim())) {
      setError('Remark is required when bulletin is Not Applicable');
      return;
    }
    if (formData.bulletinApplicable && formData.recurrenceType === 'RECURRING') {
      const hasValid = formData.schedules.some(s => s.recurrenceBasedOn && (s.recurrenceValue !== '' && s.recurrenceValue != null));
      if (!hasValid) {
        setError('For Recurring, at least one schedule row must have Based On and Value');
        return;
      }
      for (const s of formData.schedules) {
        if (!s.recurrenceBasedOn || (s.recurrenceValue === '' || s.recurrenceValue == null)) continue;
        const matchType = s.matchType || 'EXACTLY';
        if (matchType === 'BETWEEN' && (s.recurrenceValue2 === '' || s.recurrenceValue2 == null)) {
          setError('For Between, both Value 1 and Value 2 are required');
          return;
        }
        if (matchType === 'BETWEEN' && Number(s.recurrenceValue) > Number(s.recurrenceValue2)) {
          setError('Value 1 must be less than or equal to Value 2');
          return;
        }
      }
    }
    if (formData.bulletinApplicable && formData.recurrenceType === 'ONE_TIME') {
      if (!formData.oneTimeStatus) {
        setError('For One-time recurrence, status is required');
        return;
      }
      if (formData.oneTimeStatus === 'COMPLETED') {
        const hasLastCarriedOut = formData.schedules.some((s) => s.recurrenceBasedOn && s.lastDoneValue !== '' && s.lastDoneValue != null);
        if (!hasLastCarriedOut) {
          setError('When One-time status is Completed, enter Last carried out value');
          return;
        }
      }
    }

    setLoading(true);
    try {
      const schedulesPayload = !formData.bulletinApplicable
        ? []
        : formData.schedules
        .filter(s => s.recurrenceBasedOn)
        .map(s => {
        const matchType = s.matchType || 'EXACTLY';
        let recurrenceValue = s.recurrenceValue;
        if (s.recurrenceBasedOn === 'HOURS' && recurrenceValue !== '' && recurrenceValue != null) {
          recurrenceValue = typeof recurrenceValue === 'number' ? Math.floor(recurrenceValue) : parseInt(String(recurrenceValue), 10);
          if (Number.isNaN(recurrenceValue)) recurrenceValue = null;
        } else if (s.recurrenceBasedOn === 'CYCLES' && recurrenceValue !== '' && recurrenceValue != null) {
          recurrenceValue = typeof recurrenceValue === 'number' ? recurrenceValue : parseFloat(recurrenceValue);
          if (Number.isNaN(recurrenceValue)) recurrenceValue = null;
        } else if (['DAYS', 'MONTHS', 'YEARS', 'LANDINGS'].includes(s.recurrenceBasedOn) && recurrenceValue !== '' && recurrenceValue != null) {
          recurrenceValue = typeof recurrenceValue === 'number' ? recurrenceValue : parseInt(String(recurrenceValue), 10);
          if (Number.isNaN(recurrenceValue)) recurrenceValue = null;
        } else if (recurrenceValue === '' || recurrenceValue == null) {
          recurrenceValue = null;
        }
        let recurrenceValue2 = null;
        if (matchType === 'BETWEEN' && (s.recurrenceValue2 !== '' && s.recurrenceValue2 != null)) {
          if (s.recurrenceBasedOn === 'HOURS') {
            recurrenceValue2 = typeof s.recurrenceValue2 === 'number' ? Math.floor(s.recurrenceValue2) : parseInt(String(s.recurrenceValue2), 10);
          } else if (s.recurrenceBasedOn === 'CYCLES') {
            recurrenceValue2 = typeof s.recurrenceValue2 === 'number' ? s.recurrenceValue2 : parseFloat(s.recurrenceValue2);
          } else {
            recurrenceValue2 = typeof s.recurrenceValue2 === 'number' ? s.recurrenceValue2 : parseInt(String(s.recurrenceValue2), 10);
          }
          if (Number.isNaN(recurrenceValue2)) recurrenceValue2 = null;
        }
        const entry = {
          recurrenceBasedOn: s.recurrenceBasedOn || null,
          matchType,
          recurrenceValue,
          recurrenceValue2,
          ...(
            (s.recurrenceBasedOn === 'CYCLES' || s.recurrenceBasedOn === 'HOURS') && s.tlbCarriedForwardKey
              ? { tlbCarriedForwardKey: String(s.tlbCarriedForwardKey).trim() }
              : {}
          ),
          lastDoneValue: s.lastDoneValue !== '' && s.lastDoneValue != null ? s.lastDoneValue : null,
          lastDoneNote: s.lastDoneNote != null && String(s.lastDoneNote).trim() ? String(s.lastDoneNote).trim() : null,
          lastDoneByAircraft: Array.isArray(s.lastDoneByAircraft)
            ? s.lastDoneByAircraft
                .filter((x) => x && x.aircraftId)
                .map((x) => ({
                  aircraftId: x.aircraftId,
                  lastDoneValue: x.lastDoneValue !== '' && x.lastDoneValue != null ? x.lastDoneValue : null,
                  lastDoneNote: x.lastDoneNote != null && String(x.lastDoneNote).trim() ? String(x.lastDoneNote).trim() : null
                }))
            : []
        };
        if (['DAYS', 'MONTHS', 'YEARS'].includes(s.recurrenceBasedOn) && entry.lastDoneValue && typeof entry.lastDoneValue === 'string' && entry.lastDoneValue.length >= 10) {
          entry.lastDoneValue = new Date(entry.lastDoneValue).toISOString();
        }
        if (['DAYS', 'MONTHS', 'YEARS'].includes(s.recurrenceBasedOn) && Array.isArray(entry.lastDoneByAircraft)) {
          entry.lastDoneByAircraft = entry.lastDoneByAircraft.map((x) => {
            if (x.lastDoneValue && typeof x.lastDoneValue === 'string' && x.lastDoneValue.length >= 10) {
              return { ...x, lastDoneValue: new Date(x.lastDoneValue).toISOString() };
            }
            return x;
          });
        }
        return entry;
        });
      const payload = {
        ...formData,
        nextDueDate: null,
        schedules: schedulesPayload,
        ...(
          !formData.bulletinApplicable
            ? {
                recurrenceType: 'ONE_TIME',
                oneTimeStatus: 'INCOMPLETE',
                recurrenceBasedOn: null,
                recurrenceValue: null,
                recurrenceValue2: null,
                matchType: 'EXACTLY'
              }
            : {}
        ),
        aircraftAssemblies: formData.aircraftAssemblies.map(a => ({
          aircraftId: a.aircraftId,
          assemblyIds: a.assemblyIds || []
        }))
      };
      if (bulletin) {
        const res = await axios.put(`/api/adsb-service-bulletins/${bulletin._id}`, payload);
        if (res.data.hasWorkOrders) {
          showToast('Bulletin updated. Existing work orders will not reflect changes.', 'warning');
        } else {
          showToast('AD/SB bulletin updated successfully', 'success');
        }
      } else {
        await axios.post('/api/adsb-service-bulletins', payload);
        showToast('AD/SB bulletin created successfully', 'success');
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to save AD/SB bulletin');
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally {
      setLoading(false);
    }
  };

  const aircraftAlreadyAdded = formData.aircraftAssemblies.map(a => (a.aircraftId && a.aircraftId.toString()) || a.aircraftId);
  const aircraftAvailableToAdd = aircraft.filter(ac => !aircraftAlreadyAdded.includes(ac._id.toString()));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly ? 'View AD/SB Service Bulletin' : (bulletin ? 'Edit AD/SB Service Bulletin' : 'Create AD/SB Service Bulletin')}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {hasWorkOrders && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This bulletin has work order(s) already created. Changes will not affect existing work orders.
          </Alert>
        )}
        {bulletin && (
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
            <Tab label="Details" />
            <Tab label="History" />
          </Tabs>
        )}
        {(tabIndex === 0 || !bulletin) && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {bulletin && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="AD/SB #"
                value={bulletin.adsbBulletinNumber || (bulletin._id ? `ADSB-${bulletin._id.toString().slice(-8).toLowerCase()}` : '')}
                InputProps={{ readOnly: true }}
                size="small"
                helperText="Reference ID (read-only)"
                sx={{ maxWidth: 240 }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>Basic Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title *"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    InputProps={{ readOnly }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    select
                    label="Classification *"
                    value={formData.classification}
                    onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                    required
                    disabled={readOnly}
                  >
                    {CLASSIFICATION_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    select
                    label="Priority *"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    required
                    disabled={readOnly}
                  >
                    {PRIORITY_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    select
                    label="Source *"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    required
                    disabled={readOnly}
                  >
                    {SOURCE_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {formData.source === 'OTHER' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Source (custom)"
                      value={formData.sourceCustom}
                      onChange={(e) => setFormData({ ...formData, sourceCustom: e.target.value })}
                      placeholder="e.g. FAA, EASA"
                      InputProps={{ readOnly }}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    fullWidth
                    select
                    label="Regulatory Authority"
                    value={formData.regulatoryAuthorityName || ''}
                    onChange={(e) => setFormData({ ...formData, regulatoryAuthorityName: e.target.value })}
                    disabled={readOnly}
                  >
                    <MenuItem value="">—</MenuItem>
                    {REGULATORY_AUTHORITY_OPTIONS.map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {formData.regulatoryAuthorityName === 'OTHER' && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Regulatory Authority (custom)"
                      value={formData.regulatoryAuthorityNameCustom}
                      onChange={(e) => setFormData({ ...formData, regulatoryAuthorityNameCustom: e.target.value })}
                      InputProps={{ readOnly }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Serial Numbers / Make-Model (as per bulletin)"
                    value={formData.serialNumbersMakeModel}
                    onChange={(e) => setFormData({ ...formData, serialNumbersMakeModel: e.target.value })}
                    multiline
                    rows={2}
                    InputProps={{ readOnly }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={2}
                    InputProps={{ readOnly }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>Bulletin-Level Applicability</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.bulletinApplicable}
                    onChange={(e) => {
                      const bulletinApplicable = e.target.checked;
                      if (bulletinApplicable) {
                        setFormData({ ...formData, bulletinApplicable });
                        return;
                      }
                      // Not Applicable: clear recurrence — schedule fields are not used
                      setFormData({
                        ...formData,
                        bulletinApplicable: false,
                        recurrenceType: 'ONE_TIME',
                        oneTimeStatus: 'INCOMPLETE',
                        schedules: [defaultScheduleRow()]
                      });
                    }}
                    disabled={readOnly}
                  />
                }
                label="Bulletin is Applicable"
              />
              {!formData.bulletinApplicable && (
                <TextField
                  fullWidth
                  label="Remark (required when Not Applicable) *"
                  value={formData.bulletinApplicabilityRemark}
                  onChange={(e) => setFormData({ ...formData, bulletinApplicabilityRemark: e.target.value })}
                  required
                  multiline
                  rows={2}
                  sx={{ mt: 1 }}
                  InputProps={{ readOnly }}
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 2 }}>Aircraft & Assemblies (per aircraft)</Typography>
              <Box sx={{ mb: 2 }}>
                {!readOnly && (
                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <InputLabel>Add Aircraft</InputLabel>
                  <Select
                    value=""
                    label="Add Aircraft"
                    onChange={(e) => addAircraft(e.target.value)}
                    disabled={!aircraftAvailableToAdd.length}
                  >
                    <MenuItem value="">Select aircraft to add</MenuItem>
                    {aircraftAvailableToAdd.map(ac => (
                      <MenuItem key={ac._id} value={ac._id}>
                        {ac.registration} – {ac.manufacturer} {ac.model}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                )}
              </Box>
              {formData.aircraftAssemblies.map((aa, index) => {
                const ac = aircraft.find(a => (a._id && a._id.toString()) === (aa.aircraftId && aa.aircraftId.toString()));
                const reg = ac ? `${ac.registration} – ${ac.manufacturer} ${ac.model}` : aa.aircraftId;
                const assemblies = assembliesByAircraft[aa.aircraftId] || [];
                const selectedAssemblies = assemblies.filter(asm => {
                  const id = asm._id && asm._id.toString();
                  return (aa.assemblyIds || []).some(s => (s && s.toString()) === id);
                });
                return (
                  <Box key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">{reg}</Typography>
                      {!readOnly && (
                        <IconButton size="small" color="error" onClick={() => removeAircraft(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Autocomplete
                      multiple
                      size="small"
                      options={assemblies}
                      value={selectedAssemblies}
                      getOptionLabel={(opt) => `${opt.typeName || 'Unknown'} – SN: ${opt.serialNumber || 'N/A'}`}
                      isOptionEqualToValue={(o, v) => (o._id && o._id.toString()) === (v._id && v._id.toString())}
                      onChange={(e, newVal) => setAssemblyIdsForAircraft(index, newVal)}
                      loading={loadingAssemblies}
                      disabled={readOnly}
                      renderInput={(params) => <TextField {...params} label="Assemblies" placeholder="Select assemblies" />}
                      renderTags={(value, getTagProps) => value.map((opt, i) => (
                        <Chip key={opt._id} label={opt.typeName || opt.serialNumber} size="small" {...getTagProps({ index: i })} />
                      ))}
                    />
                  </Box>
                );
              })}
              {formData.aircraftAssemblies.length === 0 && (
                <Typography variant="body2" color="text.secondary">Add at least one aircraft and optionally select assemblies per aircraft.</Typography>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, opacity: formData.bulletinApplicable ? 1 : 0.6 }}>
              <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 1 }}>Recurrence</Typography>
              {!formData.bulletinApplicable && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Recurrence is not required when the bulletin is Not Applicable.
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Type"
                    value={formData.recurrenceType}
                    onChange={(e) => {
                      const recurrenceType = e.target.value;
                      setFormData({
                        ...formData,
                        recurrenceType,
                        oneTimeStatus: recurrenceType === 'ONE_TIME' ? (formData.oneTimeStatus || 'INCOMPLETE') : ''
                      });
                    }}
                    disabled={readOnly || !formData.bulletinApplicable}
                  >
                    <MenuItem value="ONE_TIME">One-time</MenuItem>
                    <MenuItem value="RECURRING">Recurring</MenuItem>
                  </TextField>
                </Grid>
                {formData.recurrenceType === 'ONE_TIME' && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        select
                        label="One-time status"
                        value={formData.oneTimeStatus || ''}
                        onChange={(e) => setFormData({ ...formData, oneTimeStatus: e.target.value })}
                        disabled={readOnly || !formData.bulletinApplicable}
                      >
                        <MenuItem value="COMPLETED">Completed</MenuItem>
                        <MenuItem value="INCOMPLETE">In Completed</MenuItem>
                      </TextField>
                    </Grid>
                  </>
                )}
                {formData.schedules.map((entry, index) => (
                  <Grid container spacing={2} key={index} alignItems="center" item xs={12}>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth sx={{ minWidth: 120 }} disabled={readOnly || !formData.bulletinApplicable}>
                        <InputLabel>Based On</InputLabel>
                        <Select
                          value={entry.recurrenceBasedOn}
                          label="Based On"
                          disabled={readOnly || !formData.bulletinApplicable}
                          onChange={(e) => {
                            const nextBasedOn = e.target.value;
                            const prevBasedOn = entry.recurrenceBasedOn;
                            const keepTlbKey =
                              (nextBasedOn === 'CYCLES' && prevBasedOn === 'CYCLES') ||
                              (nextBasedOn === 'HOURS' && prevBasedOn === 'HOURS');
                            const next = formData.schedules.map((s, i) =>
                              i === index
                                ? {
                                    ...s,
                                    recurrenceBasedOn: nextBasedOn,
                                    recurrenceValue: '',
                                    recurrenceValue2: '',
                                    lastDoneValue: '',
                                    lastDoneNote: '',
                                    lastDoneByAircraft: [],
                                    ...(!keepTlbKey ? { tlbCarriedForwardKey: '' } : {})
                                  }
                                : s
                            );
                            setFormData({ ...formData, schedules: next });
                          }}
                        >
                          <MenuItem value="HOURS">Hours</MenuItem>
                          <MenuItem value="CYCLES">Cycles</MenuItem>
                          <MenuItem value="LANDINGS">Landings</MenuItem>
                          <MenuItem value="DAYS">Days</MenuItem>
                          <MenuItem value="MONTHS">Months</MenuItem>
                          <MenuItem value="YEARS">Years</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {entry.recurrenceBasedOn === 'CYCLES' && (
                      <Grid item xs={12}>
                        <ScheduleTlbCycleCounterField
                          aircraftId={primaryAircraftIdForTlb}
                          currentUsageTotals={currentUsageTotals}
                          value={entry.tlbCarriedForwardKey || ''}
                          onChange={(key) => handleScheduleChange(index, 'tlbCarriedForwardKey', key)}
                          disabled={readOnly || !formData.bulletinApplicable}
                          dialogOpen={open}
                        />
                      </Grid>
                    )}
                    {entry.recurrenceBasedOn === 'HOURS' && (
                      <Grid item xs={12}>
                        <ScheduleTlbHoursCounterField
                          aircraftId={primaryAircraftIdForTlb}
                          currentUsageTotals={currentUsageTotals}
                          value={entry.tlbCarriedForwardKey || ''}
                          onChange={(key) => handleScheduleChange(index, 'tlbCarriedForwardKey', key)}
                          disabled={readOnly || !formData.bulletinApplicable}
                          dialogOpen={open}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={{ minWidth: 100 }} disabled={readOnly || !formData.bulletinApplicable}>
                        <InputLabel>Matches</InputLabel>
                        <Select
                          value={entry.matchType || 'EXACTLY'}
                          label="Matches"
                          disabled={readOnly || !formData.bulletinApplicable}
                          onChange={(e) => {
                            const nextMatch = e.target.value;
                            const next = formData.schedules.map((s, i) =>
                              i === index ? { ...s, matchType: nextMatch, ...(nextMatch === 'EXACTLY' ? { recurrenceValue2: '' } : {}) } : s
                            );
                            setFormData({ ...formData, schedules: next });
                          }}
                        >
                          <MenuItem value="EXACTLY">Exactly</MenuItem>
                          <MenuItem value="BETWEEN">Between</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {entry.recurrenceBasedOn && entry.matchType === 'BETWEEN' ? (
                      <>
                        <Grid item xs={12} md={2}>
                          {entry.recurrenceBasedOn === 'CYCLES' ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Value 1"
                              value={entry.recurrenceValue === '' ? '' : entry.recurrenceValue}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') { handleScheduleChange(index, 'recurrenceValue', ''); return; }
                                const allowed = raw.replace(/[^0-9.]/g, '');
                                const parts = allowed.split('.');
                                const intPart = (parts[0] || '').slice(0, 8);
                                const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                                handleScheduleChange(index, 'recurrenceValue', decPart ? `${intPart}.${decPart}` : intPart);
                              }}
                              placeholder="123456.123"
                            />
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              label="Value 1"
                              type="number"
                              value={entry.recurrenceValue === '' || entry.recurrenceValue == null ? '' : Math.floor(Number(entry.recurrenceValue))}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') { handleScheduleChange(index, 'recurrenceValue', ''); return; }
                                const num = parseInt(v, 10);
                                if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue', num);
                              }}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          )}
                        </Grid>
                        <Grid item xs={12} md={2}>
                          {entry.recurrenceBasedOn === 'CYCLES' ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Value 2"
                              value={entry.recurrenceValue2 === '' ? '' : entry.recurrenceValue2}
                              onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === '') { handleScheduleChange(index, 'recurrenceValue2', ''); return; }
                                const allowed = raw.replace(/[^0-9.]/g, '');
                                const parts = allowed.split('.');
                                const intPart = (parts[0] || '').slice(0, 8);
                                const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                                handleScheduleChange(index, 'recurrenceValue2', decPart ? `${intPart}.${decPart}` : intPart);
                              }}
                              placeholder="123456.123"
                            />
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              label="Value 2"
                              type="number"
                              value={entry.recurrenceValue2 === '' || entry.recurrenceValue2 == null ? '' : Math.floor(Number(entry.recurrenceValue2))}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '') { handleScheduleChange(index, 'recurrenceValue2', ''); return; }
                                const num = parseInt(v, 10);
                                if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue2', num);
                              }}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          )}
                        </Grid>
                      </>
                    ) : entry.recurrenceBasedOn ? (
                      <Grid item xs={12} md={4}>
                        {entry.recurrenceBasedOn === 'CYCLES' ? (
                          <TextField
                            fullWidth
                            size="small"
                            label="Value"
                            value={entry.recurrenceValue === '' ? '' : entry.recurrenceValue}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') { handleScheduleChange(index, 'recurrenceValue', ''); return; }
                              const allowed = raw.replace(/[^0-9.]/g, '');
                              const parts = allowed.split('.');
                              const intPart = (parts[0] || '').slice(0, 8);
                              const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                              handleScheduleChange(index, 'recurrenceValue', decPart ? `${intPart}.${decPart}` : intPart);
                            }}
                            placeholder="123456.123"
                          />
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            label="Value"
                            type="number"
                            value={entry.recurrenceValue === '' || entry.recurrenceValue == null ? '' : (Number.isNaN(Number(entry.recurrenceValue)) ? '' : Math.floor(Number(entry.recurrenceValue)))}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === '') { handleScheduleChange(index, 'recurrenceValue', ''); return; }
                              const num = parseInt(v, 10);
                              if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue', num);
                            }}
                            placeholder={entry.recurrenceBasedOn === 'HOURS' ? '1500' : '0'}
                            inputProps={{ min: 0, step: 1 }}
                          />
                        )}
                      </Grid>
                    ) : null}
                    <Grid item xs={12} md={1}>
                      {!readOnly && formData.bulletinApplicable && formData.schedules.length > 1 ? (
                        <IconButton color="error" onClick={() => handleRemoveSchedule(index)} aria-label="Remove schedule" size="small">
                          <DeleteOutlineIcon />
                        </IconButton>
                      ) : null}
                    </Grid>
                  </Grid>
                ))}
                {formData.bulletinApplicable && formData.recurrenceType === 'ONE_TIME' && formData.oneTimeStatus === 'COMPLETED' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Last carried out
                    </Typography>
                    <Grid container spacing={2}>
                      {formData.schedules.map((entry, index) => (
                        <React.Fragment key={`last-carried-${index}`}>
                          <ScheduleRowLastPerformedFields
                            row={entry}
                            scheduleIndex={index}
                            readOnly={readOnly}
                            variant="grid"
                            onScheduleChange={handleScheduleChange}
                          />
                        </React.Fragment>
                      ))}
                    </Grid>
                  </Grid>
                )}
                {!readOnly && formData.bulletinApplicable && (
                  <Grid item xs={12}>
                    <Button startIcon={<AddIcon />} onClick={handleAddSchedule} variant="outlined" size="small">
                      Add schedule
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

{bulletin?.scheduleRemaining?.some((s) => s.basedOn && s.rows?.length > 0) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Remaining until due (live)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Calendar rows show days remaining to the due date or window. Hours, cycles, and landings use approved Technical Log Book totals and last performed values; the second line is an estimated calendar date using average daily usage (aligned with AMP schedules). Edit per-aircraft last performed values directly here, then save to refresh remaining.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Schedule row</TableCell>
                        <TableCell>Aircraft</TableCell>
                        <TableCell>Last performed at</TableCell>
                        <TableCell>Remaining</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {bulletin.scheduleRemaining.flatMap((block) => {
                        if (!block.basedOn || !block.rows?.length) return [];
                        const s = formData.schedules[block.scheduleIndex];
                        const rowLabel = s?.recurrenceBasedOn
                          ? `${s.recurrenceBasedOn} ${s.matchType === 'BETWEEN' ? `${s.recurrenceValue}–${s.recurrenceValue2}` : s.recurrenceValue}`
                          : `Row ${block.scheduleIndex + 1}`;
                        return block.rows.map((r, j) => {
                          const perAircraft = (s?.lastDoneByAircraft || []).find((x) => x?.aircraftId?.toString() === r?.aircraftId?.toString());
                          const rowForAircraft = s
                            ? {
                                ...s,
                                lastDoneValue: perAircraft ? perAircraft.lastDoneValue : s.lastDoneValue,
                                lastDoneNote: perAircraft ? perAircraft.lastDoneNote : s.lastDoneNote
                              }
                            : null;
                          return (
                          <TableRow key={`${block.scheduleIndex}-${j}`}>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {j === 0 ? (
                                <Box>
                                  <Typography variant="body2">{rowLabel}</Typography>
                                  {!readOnly && r.aircraftId ? (
                                    <Button
                                      size="small"
                                      sx={{ mt: 0.5, px: 0 }}
                                      onClick={() => applyScheduleLastDoneToAllAircraft(block.scheduleIndex, r.aircraftId)}
                                    >
                                      Apply this to all aircraft
                                    </Button>
                                  ) : null}
                                </Box>
                              ) : ''}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>{r.registration}</TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              {readOnly ? (
                                <Tooltip title={r.lastPerformedAtTooltip || ''} placement="left">
                                  <Box>
                                    <Typography variant="body2" component="span">
                                      {r.lastPerformedAtDisplay ?? '—'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              ) : rowForAircraft && isScheduleRowValid(rowForAircraft) && r.aircraftId ? (
                                <ScheduleRowLastPerformedFields
                                  row={rowForAircraft}
                                  scheduleIndex={block.scheduleIndex}
                                  readOnly={false}
                                  variant="compact"
                                  onScheduleChange={(scheduleIndex, field, value) => handleScheduleAircraftLastDoneChange(scheduleIndex, r.aircraftId, field, value)}
                                />
                              ) : (
                                <Tooltip title={r.lastPerformedAtTooltip || ''} placement="left">
                                  <Box>
                                    <Typography variant="body2" component="span">
                                      {r.lastPerformedAtDisplay ?? '—'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <Tooltip title={r.tooltip || ''} placement="left">
                                <Box>
                                  <Typography variant="body2" component="span">
                                    {r.primaryRemaining}
                                  </Typography>
                                  {r.secondaryRemaining ? (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {r.secondaryRemaining}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          );
                        });
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}


          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <ADSBChecksManager
                checks={formData.adsbChecks}
                onChange={(checks) => setFormData({ ...formData, adsbChecks: checks })}
                readOnly={readOnly}
              />
            </Paper>
          </Grid>

          {bulletin && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 1 }}>Evidence</Typography>
                {bulletin.evidence && bulletin.evidence.length > 0 && (
                  <List dense sx={{ mb: 1 }}>
                    {bulletin.evidence.map((e, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={e.originalFileName || e.fileName} />
                        <ListItemSecondaryAction>
                          <Button size="small" href={e.fileUrl} target="_blank" rel="noopener noreferrer">Open</Button>
                          {!readOnly && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleEvidenceDelete(e.fileName)}
                              disabled={deletingEvidence === e.fileName}
                              aria-label="Remove evidence file"
                              sx={{ ml: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
                {!readOnly && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button variant="outlined" size="small" component="label" startIcon={<AttachFile />} disabled={uploadingEvidence}>
                      {uploadingEvidence ? 'Uploading...' : 'Upload evidence file'}
                      <input type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt" onChange={handleEvidenceUpload} />
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
        )}
        {bulletin && tabIndex === 1 && (
          <Box sx={{ mt: 2 }}>
            {historyLoading && (
              <Typography variant="body2" color="text.secondary">Loading history...</Typography>
            )}
            {historyError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError(null)}>
                {historyError}
              </Alert>
            )}
            {!historyLoading && !historyError && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Audit timeline
                    </Typography>
                    {historyEvents.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No history records.</Typography>
                    ) : (
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {historyEvents.map((ev) => (
                          <Box component="li" key={ev.id} sx={{ mb: 1.5 }}>
                            <Typography variant="body2">
                              <strong>{ev.action}</strong>
                              {ev.date && ` — ${new Date(ev.date).toLocaleString()}`}
                              {ev.who && ` by ${ev.who.firstName || ''} ${ev.who.lastName || ''}`.trim()}
                            </Typography>
                            {ev.summary && ev.summary.length > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                                {ev.summary.join('; ')}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Intimations
                    </Typography>
                    {historyIntimations.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No intimations linked to this AD/SB.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Intimation ID</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Fulfilled by</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {historyIntimations.map((int) => (
                              <TableRow key={int._id}>
                                <TableCell sx={{ fontFamily: 'monospace' }}>#{String(int._id).slice(-6)}</TableCell>
                                <TableCell><Chip label={int.status || '—'} size="small" /></TableCell>
                                <TableCell>{int.createdAt ? new Date(int.createdAt).toLocaleDateString() : '—'}</TableCell>
                                <TableCell>
                                  {int.workOrderNumber || (int.linkedWorkOrderId?.workOrderNumber)
                                    ? `WO #${int.workOrderNumber || int.linkedWorkOrderId?.workOrderNumber}`
                                    : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {readOnly ? (
          <>
            <Button onClick={onClose}>Close</Button>
            {onSwitchToEdit && (
              <Button variant="contained" startIcon={<EditIcon />} onClick={onSwitchToEdit}>
                Edit
              </Button>
            )}
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                loading ||
                !formData.title?.trim() ||
                !formData.classification ||
                !formData.priority ||
                !formData.source ||
                formData.aircraftAssemblies.length === 0 ||
                !formData.adsbChecks?.length ||
                (!formData.bulletinApplicable && !formData.bulletinApplicabilityRemark?.trim()) ||
                (formData.bulletinApplicable && formData.recurrenceType === 'RECURRING' && !formData.schedules.some(s => s.recurrenceBasedOn && (s.recurrenceValue !== '' && s.recurrenceValue != null))) ||
                (formData.bulletinApplicable && formData.recurrenceType === 'ONE_TIME' && !formData.oneTimeStatus) ||
                (formData.bulletinApplicable && formData.recurrenceType === 'ONE_TIME' && formData.oneTimeStatus === 'COMPLETED' && !formData.schedules.some((s) => s.recurrenceBasedOn && s.lastDoneValue !== '' && s.lastDoneValue != null))
              }
            >
              {loading ? 'Saving...' : bulletin ? 'Update' : 'Create'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditADSBulletinDialog;
